'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Icon, Spinner } from '@/src/components/ui';
import { CircularCharCounter } from '@/src/components/ui/CircularCharCounter';
import { POST_MEDIA_LIMITS, URL_REGEX } from '@/src/constants/constants';
import { cn } from '@workspace/ui/lib/utils';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Hashtag } from '@/src/utils/hashtag-extension';
import { Button } from '@workspace/ui/components/button';
import { useBoolean } from '@/src/hooks/use-boolean';
import { EmojiPicker } from '@/src/components/ui/EmojiPicker';
import { useCreateSocialPostCommentMutation } from '@/src/hooks/use-comment-mutation';
import { useProfile } from '@/src/hooks/use-profile';
import { ISocialPostComment } from 'types/src/types';
import { SocialCommentItem } from './SocialCommentItem';
import { AuthorBlock } from '../AuthorBlock';
import { useOptionalCommentScroll } from './CommentScrollContext';
import { useQueryClient } from '@tanstack/react-query';
import { makeMentionExtension } from '@/src/utils/tiptap/makeMentionExtension';

type Props = {
  handleCloseModal: () => void;
  postId: number;
  onCreated?: (id: number) => void;
  replyToComment: ISocialPostComment | null;
  parentCommentId: number | null;
};

export const ReplyForm = ({
  handleCloseModal,
  postId,
  replyToComment,
  parentCommentId,
  onCreated,
}: Props) => {
  const [charCount, setCharCount] = useState(0);
  const [replyText, setReplyText] = useState('');
  const isLoading = useBoolean(false);
  const mentionLoadingRef = useRef<(v: boolean) => void>(() => {});

  const scrollCtx = useOptionalCommentScroll();
  const qc = useQueryClient();

  const [tagged, setTagged] = useState<{
    id: number;
    uniqueLink: string;
  } | null>(null);

  const { data: profile, isLoading: profileLoading } = useProfile();

  const lastValidContentRef = useRef<{ html: string; text: string }>({
    html: replyText || '',
    text: '',
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Hashtag,
      Placeholder.configure({
        placeholder: ({ editor }) => {
          const doc = editor.state.doc;
          const onlyOneBlock = doc.childCount === 1;
          const first = doc.firstChild;
          const firstIsEmptyTextBlock =
            !!first && (first as any).isTextblock && first.content.size === 0;

          const isEditorEmpty = onlyOneBlock && firstIsEmptyTextBlock;
          const placeholderText = replyToComment
            ? `Reply to ${replyToComment.author.uniqueLink}`
            : 'Write a reply...';
          return isEditorEmpty ? placeholderText : '';
        },
        showOnlyWhenEditable: true,
      }),
      makeMentionExtension({
        postId,
        fetchUrl: (q) =>
          `/api/social-post/suggestions?q=${encodeURIComponent(q)}&postId=${postId ?? ''}`,
        onPicked: (item) => {
          setTagged({ id: item.id, uniqueLink: item.uniqueLink });
        },
      }),
    ],
    content: replyText || '',
    onUpdate: ({ editor }) => {
      const plain = editor.getText() ?? '';

      if (plain.length > POST_MEDIA_LIMITS.MAX_CHARS) {
        const { html } = lastValidContentRef.current;
        editor.commands.setContent(html, false);
        return;
      }

      if (URL_REGEX.test(plain)) {
        const { html } = lastValidContentRef.current;
        editor.commands.setContent(html, false);

        return;
      }

      const html = editor.getHTML();

      lastValidContentRef.current = {
        html,
        text: plain,
      };

      setCharCount(plain.length);
      setReplyText(html);
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;
    if (!replyToComment) return;

    const plain = editor.getText()?.trim() ?? '';
    if (plain.length > 0) return;

    const u = replyToComment.author;
    if (!u?.uniqueLink) return;

    setTagged({ id: u.id, uniqueLink: u.uniqueLink });

    editor
      .chain()
      .focus()
      .insertContent([
        {
          type: 'mention',
          attrs: { id: u.uniqueLink, label: u.uniqueLink },
        },
        { type: 'text', text: ' ' },
      ])
      .run();

    const html = editor.getHTML();
    const text = editor.getText() ?? '';
    lastValidContentRef.current = { html, text };
    setReplyText(html);
    setCharCount(text.length);
  }, [editor, replyToComment]);

  const boldActive = editor?.isActive('bold');
  const italicActive = editor?.isActive('italic');
  const underlineActive = editor?.isActive('underline');

  const { mutate, isPending } = useCreateSocialPostCommentMutation();

  const handleCreateReply = () => {
    if (lastValidContentRef.current.text.trim().length === 0) return;
    const replyTargetId = replyToComment?.author?.id ?? null;
    const replyTargetLink = replyToComment?.author?.uniqueLink ?? null;

    mutate(
      {
        postId,
        content: lastValidContentRef.current.html,
        parentId: parentCommentId,
        replyToCommentId: replyToComment?.id,
        taggedUserId: tagged?.id ?? replyTargetId,
        taggedUserUniqueLink: tagged?.uniqueLink ?? replyTargetLink,
      },
      {
        onSuccess: (result: any) => {
          const created = result?.data?.comment ?? result?.data ?? result;

          if (scrollCtx) {
            if (parentCommentId) scrollCtx.requestOpenThread(parentCommentId);
            scrollCtx.scrollToWhenReady(created.id, { highlight: false });
          }

          if (parentCommentId) {
            qc.invalidateQueries({
              queryKey: ['postComments', postId, 'replies', parentCommentId],
            });
          }

          if (scrollCtx) {
            if (parentCommentId) scrollCtx.requestOpenThread(parentCommentId);
            scrollCtx.scrollToWhenReady(created.id, { highlight: false });
          }

          onCreated?.(created.id);

          handleCloseModal();
          setReplyText('');
          editor?.commands.setContent('');
          setCharCount(0);
        },
      },
    );
  };

  const loading = isLoading.value || isPending;

  return (
    <div className="w-full relative flex flex-col md:max-h-[630px] md:py-5 py-3">
      <div className="flex md:sticky justify-between items-center md:px-4 px-3">
        <Icon
          name="close"
          className="size-2.5 hover:cursor-pointer"
          onClick={handleCloseModal}
        />
        <h4 className="md:text-lg! font-semibold">Reply</h4>
        <CircularCharCounter
          current={charCount}
          max={POST_MEDIA_LIMITS.MAX_CHARS}
          isPostForm
          size={32}
        />
      </div>
      <hr className="md:mt-2" />
      {replyToComment ? (
        <>
          <SocialCommentItem
            comment={replyToComment}
            isMyComment={profile?.id === replyToComment.author.id}
            isAbleToSuperLike={false}
            isReply={false}
            isMyPost={false}
            isReplyModal={true}
          />
          <>
            <div className="flex gap-4 px-3 pt-3">
              <Image
                src={
                  (!profileLoading && profile?.avatarUrl) ||
                  '/images/default-avatar.png'
                }
                alt={profile?.fullName || 'User Avatar'}
                width={42}
                className="size-[42px]"
                height={42}
              />

              <div className="flex flex-col">
                <AuthorBlock
                  name={profile?.fullName || ''}
                  uniqueLink={profile?.uniqueLink || ''}
                  level={profile?.level || 0}
                  createdAt={new Date()}
                />

                <div
                  id="editor-container"
                  className={cn('w-full relative min-h-20', {
                    '': charCount === 0,
                  })}
                >
                  {editor && (
                    <EditorContent
                      editor={editor}
                      className="min-h-fit py-2 outline-none prose prose-sm max-w-full rich-text-editor placeholder:text-gray-dark placeholder:font-medium"
                    />
                  )}
                  <div className="my-3"></div>
                </div>
              </div>
            </div>
            <hr className="h-1 md:inline hidden" />
          </>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0 md:px-3">
          <div className="md:p-6 p-3">
            <div className="flex md:gap-4 gap-1">
              <div className="size-[42px]">
                <Image
                  src={
                    (!profileLoading && profile?.avatarUrl) ||
                    '/images/default-avatar.png'
                  }
                  alt={profile?.fullName || 'User Avatar'}
                  width={42}
                  height={42}
                />
              </div>

              <div
                id="editor-container"
                className={cn('w-full relative min-h-20', {
                  '': charCount === 0,
                })}
              >
                {editor && (
                  <EditorContent
                    editor={editor}
                    className="min-h-fit px-3 py-2 outline-none prose prose-sm max-w-none rich-text-editor placeholder:text-gray-dark placeholder:font-medium"
                  />
                )}
                <div className="my-3"></div>
              </div>
            </div>
            <hr className="h-1 md:inline hidden" />
          </div>
        </div>
      )}
      <div className="flex justify-between items-center px-10">
        <div className="flex gap-4 items-center">
          <EmojiPicker
            isReply={true}
            onSelect={(emoji) =>
              editor?.chain().focus().insertContent(emoji).run()
            }
          >
            <Icon
              name="smile"
              className="size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer"
            />
          </EmojiPicker>

          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Icon
              name="bold"
              className={cn(
                'size-4 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                {
                  'text-pink-accent': boldActive,
                },
              )}
            />
          </button>

          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Icon
              name="italic"
              className={cn(
                'size-4 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                {
                  'text-pink-accent': italicActive,
                },
              )}
            />
          </button>

          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
          >
            <Icon
              name="underline"
              className={cn(
                'size-5 text-blue-primary hover:text-pink-accent hover:cursor-pointer',
                {
                  'text-pink-accent': underlineActive,
                },
              )}
            />
          </button>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleCreateReply}
          className="px-5 h-8 rounded-2xl text-xs!"
          disabled={
            lastValidContentRef.current.text.trim().length === 0 || loading
          }
        >
          {loading ? <Spinner /> : 'Post'}
        </Button>
      </div>
    </div>
  );
};
